/**
 * Product FAQ - JavaScript Controller
 * Handles accordion behavior and accessibility
 */

class ProductFAQ extends HTMLElement {
  constructor() {
    super();
    this.faqs = [];
    this.openFirst = this.dataset.openFirst === 'true';
  }

  connectedCallback() {
    this.faqs = Array.from(this.querySelectorAll('.product-faq__item'));
    this.initAccordion();
  }

  initAccordion() {
    if (this.faqs.length === 0) return;

    // Open first FAQ if setting enabled
    if (this.openFirst && this.faqs[0]) {
      this.faqs[0].open = true;
    }

    this.faqs.forEach((faq, index) => {
      const summary = faq.querySelector('.product-faq__question');
      
      if (!summary) return;

      // Add ARIA attributes
      const answerId = `faq-answer-${index}`;
      const answer = faq.querySelector('.product-faq__answer');
      
      if (answer) {
        answer.id = answerId;
        summary.setAttribute('aria-controls', answerId);
      }

      // Enhanced click handler for analytics
      summary.addEventListener('click', () => {
        // Track FAQ interaction if analytics available
        if (window.gtag) {
          const question = summary.querySelector('.product-faq__question-text')?.textContent;
          window.gtag('event', 'faq_interaction', {
            question: question,
            action: faq.open ? 'close' : 'open'
          });
        }

        // Add smooth animation class
        requestAnimationFrame(() => {
          if (faq.open) {
            faq.classList.add('is-closing');
            setTimeout(() => {
              faq.classList.remove('is-closing');
            }, 300);
          } else {
            faq.classList.add('is-opening');
            setTimeout(() => {
              faq.classList.remove('is-opening');
            }, 300);
          }
        });
      });

      // Keyboard navigation
      summary.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.focusPrevious(index);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.focusNext(index);
        }
      });
    });
  }

  focusPrevious(currentIndex) {
    const prevIndex = currentIndex === 0 ? this.faqs.length - 1 : currentIndex - 1;
    const prevFaq = this.faqs[prevIndex];
    prevFaq.querySelector('.product-faq__question')?.focus();
  }

  focusNext(currentIndex) {
    const nextIndex = currentIndex === this.faqs.length - 1 ? 0 : currentIndex + 1;
    const nextFaq = this.faqs[nextIndex];
    nextFaq.querySelector('.product-faq__question')?.focus();
  }
}

customElements.define('product-faq', ProductFAQ);

/**
 * Category tab switch — filters sibling `.product-faq__item` elements by
 * `data-category`. Items with no category are treated as "always visible"
 * so pages that haven't categorised their FAQs are unaffected.
 */
class FAQCategorySwitch extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('.product-faq__tab'));
    this.list = this.parentElement?.querySelector('.product-faq__list');

    if (!this.list || this.tabs.length === 0) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectCategory(tab));
    });

    const activeTab = this.tabs.find((tab) => tab.classList.contains('is-active')) || this.tabs[0];
    this.filterItems(activeTab.dataset.category);
  }

  selectCategory(selectedTab) {
    if (selectedTab.classList.contains('is-active')) return;

    this.tabs.forEach((tab) => {
      const isSelected = tab === selectedTab;
      tab.classList.toggle('is-active', isSelected);
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    this.filterItems(selectedTab.dataset.category);
  }

  filterItems(category) {
    this.list.querySelectorAll('.product-faq__item').forEach((item) => {
      const itemCategory = item.dataset.category || '';
      const matches = itemCategory === '' || itemCategory === category;
      item.hidden = !matches;
    });
  }
}

customElements.define('faq-category-switch', FAQCategorySwitch);

/**
 * Initialize FAQ sections on page load
 */
document.addEventListener('DOMContentLoaded', () => {
  const faqElements = document.querySelectorAll('.product-faq');

  faqElements.forEach((element) => {
    // Check for open_first setting from schema
    const section = element.closest('.product-faq-section');
    if (section) {
      element.dataset.openFirst = section.dataset.openFirst || 'true';
    }
  });
});
